/**
 * Hex Renderer
 * 
 * Utilities for rendering hexagons on canvas
 */

import { AxialCoord, TerrainType, TERRAIN_COLORS } from '../types/terrain';
import { hexToPixel, hexCorners, hexToPixelIsometric, hexCornersIsometric } from '../utils/hexMath';
import { getHexTilePath, loadImage, imageCache } from '../utils/imageLoader';

/**
 * Faction color mapping for territory highlights
 */
export const FACTION_COLORS: Record<string, string> = {
  'highland-clans': '#8b6f47',      // Brown-gray (mountain/highland)
  'stoneheart-clans': '#7a8b99',    // Stone gray-blue
  'sea-kings': '#5a9fb5',           // Sea blue
  'hearth-folk': '#c9a66b',         // Bronze/copper (mining)
  'wildwood-folk': '#4a6b3a',       // Deep forest green
  'riverbend-kingdoms': '#8fbc5a',  // River valley green
};

/**
 * Draw a single hex on canvas
 * Supports both image textures and color fills
 */
export async function drawHex(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  terrain: TerrainType,
  hexSize: number,
  showBorder: boolean = true,
  useImages: boolean = true
): Promise<void> {
  const center = hexToPixel(coord, hexSize);
  const corners = hexCorners(coord, hexSize);
  
  // Create hex path
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  
  // Try to use image texture if enabled
  if (useImages) {
    try {
      const imagePath = getHexTilePath(terrain, coord.q, coord.r);
      const img = await loadImage(imagePath);
      
      // Clip to hex shape and draw image
      ctx.save();
      ctx.clip();
      
      // Calculate image dimensions to fill hex
      const hexWidth = hexSize * 2;
      const hexHeight = hexSize * Math.sqrt(3);
      
      // Draw image centered on hex
      ctx.drawImage(
        img,
        center.x - hexWidth / 2,
        center.y - hexHeight / 2,
        hexWidth,
        hexHeight
      );
      
      ctx.restore();
    } catch (error) {
      // Fallback to color if image fails to load
      const color = TERRAIN_COLORS[terrain];
      ctx.fillStyle = color;
      ctx.fill();
    }
  } else {
    // Use color fill
    const color = TERRAIN_COLORS[terrain];
    ctx.fillStyle = color;
    ctx.fill();
  }
  
  // Draw border (use lighter color for ocean hexes so they're visible)
  if (showBorder) {
    ctx.strokeStyle = terrain === TerrainType.Ocean ? '#2a4a6a' : '#333';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

/**
 * Draw hex synchronously (for immediate rendering)
 * Uses cached images if available, falls back to color
 */
export function drawHexSync(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  terrain: TerrainType,
  hexSize: number,
  showBorder: boolean = true
): void {
  const center = hexToPixel(coord, hexSize);
  const corners = hexCorners(coord, hexSize);
  
  // Create hex path
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  
  // Try to use cached image
  const imagePath = getHexTilePath(terrain, coord.q, coord.r);
  const cachedImg = imageCache.get(imagePath);
  
  if (cachedImg && cachedImg.complete) {
    // Use cached image
    ctx.save();
    ctx.clip();
    
    const hexWidth = hexSize * 2;
    const hexHeight = hexSize * Math.sqrt(3);
    
    ctx.drawImage(
      cachedImg,
      center.x - hexWidth / 2,
      center.y - hexHeight / 2,
      hexWidth,
      hexHeight
    );
    
    ctx.restore();
  } else {
    // Fallback to color
    const color = TERRAIN_COLORS[terrain];
    ctx.fillStyle = color;
    ctx.fill();
    
    // Try to load image for next render (async, won't block)
    if (!cachedImg) {
      loadImage(imagePath).then(img => {
        imageCache.set(imagePath, img);
      }).catch(() => {
        // Image failed to load, that's okay
      });
    }
  }
  
  // Draw border (use lighter color for ocean hexes so they're visible)
  if (showBorder) {
    ctx.strokeStyle = terrain === TerrainType.Ocean ? '#2a4a6a' : '#333';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

/**
 * Draw hex grid lines
 */
export function drawHexGrid(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  hexSize: number,
  color: string = '#666',
  lineWidth: number = 1
): void {
  const corners = hexCorners(coord, hexSize);
  
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

/**
 * Draw hex with highlight
 */
export function drawHighlightedHex(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  hexSize: number,
  color: string = '#ffff00',
  alpha: number = 0.3
): void {
  const corners = hexCorners(coord, hexSize);
  
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;
  ctx.fill();
  ctx.globalAlpha = 1.0;
  
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();
}

/**
 * Draw text at hex center
 */
export function drawHexText(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  text: string,
  hexSize: number,
  color: string = '#000',
  fontSize: number = 12
): void {
  const center = hexToPixel(coord, hexSize);
  
  ctx.fillStyle = color;
  ctx.font = `${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, center.x, center.y);
}

/**
 * Clear canvas
 */
export function clearCanvas(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.clearRect(0, 0, width, height);
}

/**
 * Fill canvas with background color
 */
export function fillBackground(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  color: string = '#1a1a1a'
): void {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Draw hex in isometric view synchronously
 * Uses cached images if available, falls back to color
 */
export function drawHexIsometricSync(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  terrain: TerrainType,
  hexSize: number,
  showBorder: boolean = true
): void {
  const center = hexToPixelIsometric(coord, hexSize);
  const corners = hexCornersIsometric(coord, hexSize);
  
  // Create hex path
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  
  // Try to use cached image
  const imagePath = getHexTilePath(terrain, coord.q, coord.r);
  const cachedImg = imageCache.get(imagePath);
  
  if (cachedImg && cachedImg.complete) {
    // Use cached image
    ctx.save();
    ctx.clip();
    
    // For isometric view, we need to account for the transformation
    // Calculate bounding box of the transformed hex
    const minX = Math.min(...corners.map(c => c.x));
    const maxX = Math.max(...corners.map(c => c.x));
    const minY = Math.min(...corners.map(c => c.y));
    const maxY = Math.max(...corners.map(c => c.y));
    
    const hexWidth = maxX - minX;
    const hexHeight = maxY - minY;
    
    ctx.drawImage(
      cachedImg,
      minX,
      minY,
      hexWidth,
      hexHeight
    );
    
    ctx.restore();
  } else {
    // Fallback to color
    const color = TERRAIN_COLORS[terrain];
    ctx.fillStyle = color;
    ctx.fill();
    
    // Try to load image for next render (async, won't block)
    if (!cachedImg) {
      loadImage(imagePath).then(img => {
        imageCache.set(imagePath, img);
      }).catch(() => {
        // Image failed to load, that's okay
      });
    }
  }
  
  // Draw border (use lighter color for ocean hexes so they're visible)
  if (showBorder) {
    ctx.strokeStyle = terrain === TerrainType.Ocean ? '#2a4a6a' : '#333';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

/**
 * Draw hex with highlight in isometric view
 */
export function drawHighlightedHexIsometric(
  ctx: CanvasRenderingContext2D,
  coord: AxialCoord,
  hexSize: number,
  color: string = '#ffff00',
  alpha: number = 0.3
): void {
  const corners = hexCornersIsometric(coord, hexSize);
  
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < corners.length; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;
  ctx.fill();
  ctx.globalAlpha = 1.0;
  
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();
}

/**
 * Draw route overlay on hexes (flat-topped)
 * Shows planned travel path with different colors for destination vs intermediate hexes
 */
export function drawRouteOverlay(
  ctx: CanvasRenderingContext2D,
  route: AxialCoord[],
  hexSize: number,
  isIsometric: boolean = false
): void {
  if (route.length < 2) return;

  // Draw each hex in route with overlay
  route.forEach((coord, index) => {
    const isStart = index === 0;
    const isDestination = index === route.length - 1;
    
    // Skip start hex (player is already there)
    if (isStart) return;
    
    // Different colors for destination vs intermediate hexes
    const color = isDestination ? '#4a7c59' : '#6b8e7d'; // Green for destination, lighter for intermediate
    const alpha = isDestination ? 0.4 : 0.25;
    
    if (isIsometric) {
      drawHighlightedHexIsometric(ctx, coord, hexSize, color, alpha);
    } else {
      const corners = hexCorners(coord, hexSize);
      
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  });
}

/**
 * Draw connecting lines showing route direction
 */
export function drawRouteLine(
  ctx: CanvasRenderingContext2D,
  route: AxialCoord[],
  hexSize: number,
  isIsometric: boolean = false
): void {
  if (route.length < 2) return;

  const hexToPixelFn = isIsometric ? hexToPixelIsometric : hexToPixel;
  
  ctx.save();
  ctx.strokeStyle = '#4a7c59';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalAlpha = 0.6;
  
  // Draw line connecting hex centers
  ctx.beginPath();
  const firstCenter = hexToPixelFn(route[0], hexSize);
  ctx.moveTo(firstCenter.x, firstCenter.y);
  
  for (let i = 1; i < route.length; i++) {
    const center = hexToPixelFn(route[i], hexSize);
    ctx.lineTo(center.x, center.y);
  }
  
  ctx.stroke();
  
  // Draw arrows showing direction
  for (let i = 0; i < route.length - 1; i++) {
    const start = hexToPixelFn(route[i], hexSize);
    const end = hexToPixelFn(route[i + 1], hexSize);
    
    // Calculate arrow position (midpoint between hexes)
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;
    
    // Calculate arrow direction
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const angle = Math.atan2(dy, dx);
    
    // Draw arrow head
    const arrowSize = 8;
    ctx.fillStyle = '#4a7c59';
    ctx.beginPath();
    ctx.moveTo(midX, midY);
    ctx.lineTo(
      midX - arrowSize * Math.cos(angle - Math.PI / 6),
      midY - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      midX - arrowSize * Math.cos(angle + Math.PI / 6),
      midY - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();
  }
  
  ctx.restore();
}

